import { FC, useMemo } from 'react'
import * as styles from 'components/features/creditCard/creditCardPage/NextPayment/NextPayment.styl'
import { formatUSCurrency } from 'helpers/formatHelpers'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { cardUpcomingPaymentsSelector, currentCCLedgerSelector } from 'components/features/creditCard/creditCardStore/selectors'
import moment from 'moment'
import { DATE_FORMAT_READABLE, DATE_FORMAT_READABLE_YEARLESS } from 'constants/datetime'
import { AutoPayPaneCTAButton, PayCardAmountPaneButton } from 'components/features/pfm/Button/FloatingPaneButtons'
import { LabeledElement } from 'components/atoms/LabeledElement/LabeledElement'
import Flex from 'components/atoms/Flex/Flex'
import LoadingOverlay from 'components/atoms/preloaders/LoadingOverlay'
import { InputToggle } from 'components/features/pfm/InputToggle/InputToggle'
import { MINIMUM_AMOUNT_DUE_HINT } from 'components/features/creditCard/creditCardStore/constants'

// For making the Status box and Credit card image the same height
const CREDITCARD_IMAGE_HEIGHT = '100px'

export const NextPayment: FC = () => {

  const ledger = useStoreSelector(currentCCLedgerSelector)
  const dueDate = moment(ledger?.payment_due_date)
  const upcomingPayments = useStoreSelector(cardUpcomingPaymentsSelector)
  const upcomingPayment = upcomingPayments ? upcomingPayments[0] : undefined
  const upcomingPaymentDate = useMemo(() => {
    if (!upcomingPayment || upcomingPayment?.schedule_type !== 'autopay') {
      return
      // return moment(upcomingAutoPayment.payment_date)
    }

    const daysBefore = upcomingPayment.days_before_due_date
    return moment(ledger?.payment_due_date)?.subtract(daysBefore, 'days')
  }, [ledger?.payment_due_date, upcomingPayment])

  const autoPayChild = useMemo(() => !upcomingPayment || !upcomingPaymentDate ? (
    <PayCardAmountPaneButton component='div' isAutoPay>
      <InputToggle isEnabled={ false } />
    </PayCardAmountPaneButton>
  ) : (
    <AutoPayPaneCTAButton theme='blue' style='asText' size='mini'>
      { formatUSCurrency(upcomingPayment.amount) }
      &nbsp;<small>on { upcomingPaymentDate.format(DATE_FORMAT_READABLE_YEARLESS) }</small>
    </AutoPayPaneCTAButton>
  ), [upcomingPayment, upcomingPaymentDate])

  const detailsChild = useMemo(() => !!ledger && (
    <Flex>
      <LabeledElement
        vertical
        label='Due Date'
        element={ <h2>{ dueDate.format(DATE_FORMAT_READABLE) }</h2> }
        align='left'
      />
      <LabeledElement
        vertical
        label='Minimum Amount Due'
        hint={ MINIMUM_AMOUNT_DUE_HINT }
        element={ formatUSCurrency(ledger.minimum_payment_remaining) }
        align='left'
      />
      <LabeledElement
        vertical
        label='Auto-pay'
        element={ autoPayChild }
        align='left'
      />
    </Flex>
  ), [ledger, dueDate, autoPayChild])


  const statusChild = useMemo(() => {
    if (!ledger) {
      return <LoadingOverlay />
    }

    const isPast = moment().isAfter(ledger.payment_due_date)
    const isNotDue = +ledger.minimum_payment_remaining <= 0 || !ledger?.payment_due_date
    const style = { maxHeight: CREDITCARD_IMAGE_HEIGHT }

    if (isNotDue) {

      return (
        <Flex theme='lightGreen' padding='defaultSize' gap='none' style={ style }>
          You have <strong style={{ whiteSpace: 'nowrap' }}>no payment due</strong>
        </Flex>
      )
    } else if (isPast) {

      return (
        <Flex theme='lightRed' padding='defaultSize' gap='none' style={ style }>
          <span>You have a <strong style={{ whiteSpace: 'nowrap' }}>payment overdue</strong>.</span>
          <br />
          Make a payment now to avoid more fees.
        </Flex>
      )
    } else {

      return (
        <Flex theme='lightOrange' padding='defaultSize' gap='none' style={ style }>
          You have a <strong style={{ whiteSpace: 'nowrap' }}>payment due soon</strong>
        </Flex>
      )
    }
  }, [ledger])

  return (
    <Flex>
      <Flex horizontal className={ styles.nextPayment }>
        { detailsChild }
        { statusChild }
      </Flex>
      <PayCardAmountPaneButton text='Make a Payment' fullWidth backButton={ null } />
    </Flex>
  )
}

